import {
  Component,
  h,
  Prop,
  Element,
  ComponentInterface,
  Event,
  State,
  EventEmitter,
  Method,
  Listen,
  Host,
} from "@stencil/core";
import { FOCUSABLE_ELEMENTS, keyCodes } from "../../utils/utils";

@Component({
  tag: "signet-menu",
  styleUrl: "signet-menu.scss",
})
export class SignetMenu implements ComponentInterface {
  $contents: HTMLElement;
  $control: HTMLElement;
  $focusableEls: HTMLElement[];
  menuId = `menu_${menuIds++}`;

  @Element() $el: HTMLElement;

  @Prop({ mutable: true }) modelValue = false;

  /**
   *The TEXT of the menu.
   * @description This is used to identify the menu in the DOM.
   * @example "Getting Started"
   */
  @Prop() text: string;
  
  /**
   * The position of the menu.
   * @description This is used to position the menu in the DOM.
   * @example "left"
   */
  @Prop() position: "left" | "right" = "left";
  /**
   * The icon of the menu.
   * @description This is used to identify the menu in the DOM.
   */
  @Prop() icon: string;
  /*
  The variant of the menu.
  @description This is used to identify the menu in the DOM.
  @example "primary"
  */
  @Prop() variant: "primary" | "outline" | "text" | "secondary" = "primary";
  /*
  * Optum Harmony's branding.
  * @description This is used to identify the menu in the DOM.
  * @example true
  */
  @Prop() harmony = false;
  @Prop() display: "solid" | "hollow" | "clear" | "link" = "solid";
  @Prop() size: "xs" | "sm" | "md" | "lg" | "xl" = "md";
  @Prop() loading: boolean;
  @Prop() disabled: boolean;
  @Prop() hideText = false;
  @Prop() hideIndicator = false;
  @Prop() megaMenu = false;
  @Prop() collapse: "xxs" | "xs" | "sm" | "md" | "lg" | "xl" = "xs";
  @Prop() helperText: string;

  @Event() shown: EventEmitter;
  @Event() hidden: EventEmitter;

  @State() isExpanded = false;
  @State() focusIndex = 0;

  @Method()
  async hide() {
    this.isExpanded = false;
    this.focusIndex = 0;
    this.hidden.emit();
    this.modelValue = false;
    this.$el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: false,
    }));
    this.$el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: false,
    }));
    setTimeout(() => this.$control.focus());
  }

  @Method()
  async show() {
    this.isExpanded = true;
    this.shown.emit();

    this.modelValue = true;
    this.$el.dispatchEvent(new CustomEvent('input', {
      bubbles: true,
      composed: true,
      detail: true,
    }));
    this.$el.dispatchEvent(new CustomEvent('update:modelValue', {
      bubbles: true,
      composed: true,
      detail: true,
    }));

    if (this.$focusableEls.length > 0)
      setTimeout(() => this.$focusableEls[0].focus(), 100);
  }

  @Listen("keydown")
  handleKeyDown(e: KeyboardEvent) {
    switch (e.keyCode) {
      case keyCodes.TAB:
        this.handleTab(e);
        break;
      case keyCodes.ESC:
        this.handleEsc();
        break;
      default:
        break;
    }
  }

  @Listen("closeMenu")
  handleCloseMenu() {
    this.hide();
  }

  componentDidRender() {
    this.$focusableEls = Array.from(
      this.$contents.querySelectorAll(FOCUSABLE_ELEMENTS)
    );
  }

  componentDidLoad() {
    window.addEventListener("click", (e: MouseEvent) => {
      if (!this.isExpanded) return;

      const $preventCloseElements = Array.from(
        document.querySelectorAll(".prevent-menu-close")
      );
      const isPreventClose =
        this.$el.contains(e.target as HTMLElement) ||
        $preventCloseElements.some((x) => x.contains(e.target as HTMLElement));

      if (isPreventClose) return;

      this.hide();
    });
  }

  private handleEsc() {
    this.hide();
  }

  private handleTab(e: KeyboardEvent) {
    if (!this.isExpanded) return;

    e.preventDefault();

    if (e.shiftKey) {
      this.handleBackwardTab();
    } else {
      this.handleForwardTab();
    }
  }

  private handleBackwardTab() {
    this.focusIndex =
      this.focusIndex === 0 ? this.$focusableEls.length - 1 : --this.focusIndex;
    this.$focusableEls[this.focusIndex].focus();
  }

  private handleForwardTab() {
    this.focusIndex =
      this.$focusableEls.length - 1 === this.focusIndex ? 0 : ++this.focusIndex;
    this.$focusableEls[this.focusIndex].focus();
  }

  private toggleMenu() {
    if (this.isExpanded) {
      this.hide();
    } else {
      this.show();
    }
  }

  render() {
    const menuClasses = {
      "signet-menu": true,
      "show-contents": this.isExpanded,
      "mega-menu": this.megaMenu,
      "menu-variant": this.variant === "primary",
      "harmony": this.harmony,
    };

    const contentClasses = {
      contents: true,
      "mega-menu-contents": this.megaMenu,
      "menu-contents": !this.megaMenu,
      [`align-${this.position}`]: !this.megaMenu,
      [`collapse-${this.collapse}`]: !this.megaMenu,
    };

    return (
      <Host class={menuClasses}>
        <signet-button
          id={`${this.menuId}-button`}
          button-class="menu-button"
          haspopup={true}
          expanded={this.isExpanded}
          controls={this.menuId}
          onClick={() => this.toggleMenu()}
          ref={(el) => (this.$control = el.querySelector("button"))}
        >
          {this.icon && <signet-icon icon={this.icon} />}
          <span class={`menu-text ${this.hideText ? "sr-only" : ""}`}>
            {this.text}
          </span>
          {!this.hideIndicator && this.variant  == "primary" && !this.harmony && (
            <signet-icon icon="keyboard_arrow_down" class="menu-icon" />
          )}
          {!this.hideIndicator && this.variant == "primary" && this.harmony && (
            <signet-icon icon="arrow_drop_down" class="menu-icon" />
          )}
          {!this.hideIndicator &&
            this.variant == "secondary" &&
            (this.isExpanded ? (
              <signet-icon icon="arrow_drop_up" class="secondary-menu-icon" />
            ) : (
              <signet-icon icon="arrow_drop_down" class="secondary-menu-icon" />
            ))}
        </signet-button>
        <div
          id={this.menuId}
          class={contentClasses}
          role={this.megaMenu ? undefined : "list"}
          aria-labelledby={`${this.menuId}-button`}
          ref={(el) => (this.$contents = el)}
        >
          {this.helperText?.length > 0 ? (
            <div class="helpertext-container">
              <p>{this.helperText}</p>
            </div>
          ) : (
            ""
          )}
          <slot />
        </div>
      </Host>
    );
  }
}

let menuIds = 0;
